// /opt/isp_erp/frontend/src/components/warehouse/EnviosEquiposPanel.jsx
import React, { useState, useEffect } from "react";
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, TablePagination,
  Button, TextField, MenuItem, Alert, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete,
  CircularProgress, Grid, Checkbox,
} from "@mui/material";
import axios from "../../api/axios";
import { mensajeError } from "../../utils/mensajesError";

const API = "/warehouse/sucursales-equipos";
const fmtFecha = (v) => (v ? new Date(v).toLocaleString("es-EC") : "—");
const ESTADO_COLOR = { EN_TRANSITO: "warning", RECIBIDO: "success", ANULADO: "default" };
const ESTADO_TEXTO = { EN_TRANSITO: "En tránsito", RECIBIDO: "Recibido", ANULADO: "Anulado" };

function useDebounced(valor, ms) {
  const [v, setV] = useState(valor);
  useEffect(() => {
    const t = setTimeout(() => setV(valor), ms);
    return () => clearTimeout(t);
  }, [valor, ms]);
  return v;
}

function descargar(datos, nombre) {
  const url = window.URL.createObjectURL(new Blob([datos], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function NuevoEnvioDialog({ ctx, onClose, onHecho }) {
  const [sucursalId, setSucursalId] = useState(ctx.sucursales.length === 1 ? ctx.sucursales[0].id : "");
  const [equipos, setEquipos] = useState([]);
  const [textoEq, setTextoEq] = useState("");
  const qEq = useDebounced(textoEq.trim(), 300);
  const [modelo, setModelo] = useState("");
  const [modelos, setModelos] = useState([]);
  const [opcionesEq, setOpcionesEq] = useState([]);
  const [totalEq, setTotalEq] = useState(0);
  const [cargandoEq, setCargandoEq] = useState(false);
  const [vinetas, setVinetas] = useState([]);
  const [textoVin, setTextoVin] = useState("");
  const qVin = useDebounced(textoVin.trim(), 300);
  const [opcionesVin, setOpcionesVin] = useState([]);
  const [entrega, setEntrega] = useState("");
  const [obs, setObs] = useState("");
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    axios.get(`${API}/equipos-disponibles`, { params: { con_modelos: true, limite: 1 } })
      .then((r) => setModelos(r.data.modelos || []))
      .catch(() => setModelos([]));
  }, []);

  useEffect(() => {
    let vivo = true;
    setCargandoEq(true);
    axios.get(`${API}/equipos-disponibles`, { params: { q: qEq || undefined, modelo: modelo || undefined, limite: 100 } })
      .then((r) => { if (vivo) { setOpcionesEq(r.data.items || []); setTotalEq(r.data.total || 0); } })
      .catch(() => { if (vivo) { setOpcionesEq([]); setTotalEq(0); } })
      .finally(() => { if (vivo) setCargandoEq(false); });
    return () => { vivo = false; };
  }, [qEq, modelo]);

  useEffect(() => {
    let vivo = true;
    axios.get(`${API}/vinetas-disponibles`, { params: { q: qVin || undefined } })
      .then((r) => { if (vivo) setOpcionesVin(r.data || []); })
      .catch(() => { if (vivo) setOpcionesVin([]); });
    return () => { vivo = false; };
  }, [qVin]);

  const idsSel = new Set(equipos.map((e) => e.id));
  const pendientes = opcionesEq.filter((e) => !idsSel.has(e.id));

  const enviar = async () => {
    setError("");
    if (!sucursalId) { setError("Elige la sucursal de destino"); return; }
    if (equipos.length === 0 && vinetas.length === 0) { setError("Elige al menos un equipo o una viñeta"); return; }
    if (equipos.length > 500 || vinetas.length > 500) { setError("Un envío admite como máximo 500 equipos y 500 viñetas"); return; }
    setEnviando(true);
    try {
      const r = await axios.post(`${API}/envios`, {
        sucursal_id: Number(sucursalId),
        equipos: equipos.map((e) => e.sn),
        vinetas: vinetas.map((v) => v.id),
        entrega_nombre: entrega.trim() || null,
        observaciones: obs.trim() || null,
      });
      onHecho(r.data);
    } catch (e) {
      setError(mensajeError(e, "No se pudo registrar el envío"));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open onClose={enviando ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Nuevo envío de equipos y viñetas</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} md={6}>
            <TextField select fullWidth size="small" label="Sucursal de destino" value={sucursalId} onChange={(e) => setSucursalId(e.target.value)}>
              {ctx.sucursales.map((s) => (<MenuItem key={s.id} value={s.id}>{s.nombre}</MenuItem>))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth size="small" label="Quién entrega (opcional)" value={entrega} onChange={(e) => setEntrega(e.target.value)} />
          </Grid>

          <Grid item xs={12}><Typography variant="subtitle2">Equipos (solo los que están libres en la bodega central)</Typography></Grid>
          <Grid item xs={12} md={4}>
            <TextField select fullWidth size="small" label="Modelo" value={modelo} onChange={(e) => setModelo(e.target.value)}>
              <MenuItem value="">Todos los modelos</MenuItem>
              {modelos.map((m) => (<MenuItem key={m.modelo} value={m.modelo}>{m.modelo} ({m.cantidad})</MenuItem>))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={8}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              filterSelectedOptions
              limitTags={3}
              options={[...equipos, ...pendientes]}
              value={equipos}
              loading={cargandoEq}
              filterOptions={(x) => x}
              getOptionLabel={(o) => `${o.sn} · ${o.modelo}`}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              inputValue={textoEq}
              onInputChange={(_, v, motivo) => { if (motivo === "input") setTextoEq(v); if (motivo === "clear") setTextoEq(""); }}
              onChange={(_, v) => setEquipos(v)}
              noOptionsText="No hay equipos libres con este filtro"
              renderInput={(params) => (<TextField {...params} size="small" label="Escribe la serie o el modelo (o escanea el código)" />)}
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
              <Button size="small" variant="outlined" disabled={pendientes.length === 0} onClick={() => setEquipos((prev) => [...prev, ...pendientes])}>
                Agregar los {pendientes.length} resultados
              </Button>
              <Button size="small" disabled={equipos.length === 0} onClick={() => setEquipos([])}>Quitar todos</Button>
              <Typography variant="body2" color="text.secondary">
                {equipos.length} seleccionado(s) · {totalEq} disponible(s) con este filtro
                {totalEq > opcionesEq.length ? ` (se muestran ${opcionesEq.length}; afina la búsqueda)` : ""}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12}><Typography variant="subtitle2">Viñetas</Typography></Grid>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={opcionesVin}
              value={vinetas}
              filterSelectedOptions
              filterOptions={(x) => x}
              getOptionLabel={(o) => `${o.serial} - ${o.nombre}`}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              inputValue={textoVin}
              onInputChange={(_, v, motivo) => { if (motivo === "input") setTextoVin(v); }}
              onChange={(_, v) => setVinetas(v)}
              noOptionsText="No hay viñetas disponibles en la bodega central"
              renderInput={(params) => (<TextField {...params} size="small" label="Viñetas (escribe para buscar)" />)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Observaciones (opcional)" value={obs} onChange={(e) => setObs(e.target.value)} />
          </Grid>
        </Grid>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={enviando}>Cancelar</Button>
        <Button variant="contained" onClick={enviar} disabled={enviando}>{enviando ? "Enviando..." : "Registrar envío"}</Button>
      </DialogActions>
    </Dialog>
  );
}

function DetalleEnvioDialog({ ctx, envioId, onClose, onCambio }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  const [marcados, setMarcados] = useState({});
  const [recibe, setRecibe] = useState("");
  const [obs, setObs] = useState("");
  const [motivo, setMotivo] = useState("");
  const [anulando, setAnulando] = useState(false);
  const [trabajando, setTrabajando] = useState(false);

  useEffect(() => {
    let vivo = true;
    axios.get(`${API}/envios/${envioId}`)
      .then((r) => {
        if (!vivo) return;
        setD(r.data);
        const todos = {};
        r.data.items.forEach((i) => { todos[i.id] = true; });
        setMarcados(todos);
      })
      .catch((e) => { if (vivo) setError(mensajeError(e, "No se pudo cargar el envío")); });
    return () => { vivo = false; };
  }, [envioId]);

  if (!d) {
    return (
      <Dialog open onClose={onClose} fullWidth maxWidth="sm">
        <DialogContent sx={{ textAlign: "center", py: 4 }}>
          {error ? <Alert severity="error">{error}</Alert> : <CircularProgress />}
        </DialogContent>
        <DialogActions><Button onClick={onClose}>Cerrar</Button></DialogActions>
      </Dialog>
    );
  }

  const suc = ctx.sucursales.find((s) => s.id === d.sucursal_id);
  const puedeRecibir = d.estado === "EN_TRANSITO" && !!(suc && suc.puede_operar);
  const puedeAnular = d.estado === "EN_TRANSITO" && ctx.puede_enviar;
  const sinMarcar = d.items.filter((i) => !marcados[i.id]).length;

  const marcarTodos = (valor) => {
    const todos = {};
    d.items.forEach((i) => { todos[i.id] = valor; });
    setMarcados(todos);
  };

  const descargarActa = async () => {
    try {
      const r = await axios.get(`${API}/envios/${d.id}/pdf`, { responseType: "blob" });
      descargar(r.data, `${d.numero}.pdf`);
    } catch (e) {
      setError("No se pudo descargar el acta");
    }
  };

  const confirmar = async () => {
    setError("");
    if (sinMarcar > 0 && obs.trim().length < 3) { setError("Hay elementos sin recibir: anota el motivo en las observaciones"); return; }
    setTrabajando(true);
    try {
      const r = await axios.post(`${API}/envios/${d.id}/recibir`, {
        recibidos: d.items.filter((i) => marcados[i.id]).map((i) => i.id),
        recibe_nombre: recibe.trim() || null,
        observaciones: obs.trim() || null,
      });
      setD(r.data);
      onCambio("Recepción registrada");
    } catch (e) {
      setError(mensajeError(e, "No se pudo registrar la recepción"));
    } finally {
      setTrabajando(false);
    }
  };

  const anular = async () => {
    setError("");
    if (motivo.trim().length < 3) { setError("Indica el motivo de la anulación"); return; }
    setTrabajando(true);
    try {
      const r = await axios.post(`${API}/envios/${d.id}/anular`, { motivo: motivo.trim() });
      setD(r.data);
      setAnulando(false);
      onCambio("Envío anulado: los equipos y viñetas volvieron a la bodega central");
    } catch (e) {
      setError(mensajeError(e, "No se pudo anular el envío"));
    } finally {
      setTrabajando(false);
    }
  };

  return (
    <Dialog open onClose={trabajando ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {d.numero} <Chip size="small" sx={{ ml: 1 }} color={ESTADO_COLOR[d.estado]} label={ESTADO_TEXTO[d.estado] || d.estado} />
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2">Destino: <strong>{d.sucursal_nombre}</strong> · {d.n_equipos} equipo(s) · {d.n_vinetas} viñeta(s)</Typography>
        <Typography variant="body2">Enviado el {fmtFecha(d.fecha_envio)} por {d.enviado_por || "—"} · entrega: {d.entrega_nombre || "—"}</Typography>
        {d.observaciones && <Typography variant="body2">Observaciones: {d.observaciones}</Typography>}
        {d.estado === "RECIBIDO" && (
          <Typography variant="body2">Recibido el {fmtFecha(d.recibido_at)} por {d.recibido_por || "—"} · recibe: {d.recibe_nombre || "—"}</Typography>
        )}
        {d.observaciones_recepcion && <Typography variant="body2">Observaciones de la recepción: {d.observaciones_recepcion}</Typography>}
        {d.estado === "ANULADO" && <Alert severity="warning" sx={{ mt: 1 }}>Anulado el {fmtFecha(d.anulado_at)}: {d.motivo_anulacion}</Alert>}

        {puedeRecibir && (
          <Box sx={{ mt: 2, display: "flex", gap: 1, alignItems: "center" }}>
            <Typography variant="subtitle2">Marca lo que llegó:</Typography>
            <Button size="small" onClick={() => marcarTodos(true)}>Marcar todo</Button>
            <Button size="small" onClick={() => marcarTodos(false)}>Desmarcar todo</Button>
          </Box>
        )}
        <TableContainer sx={{ mt: 1, maxHeight: 360 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {puedeRecibir && <TableCell padding="checkbox" />}
                <TableCell>Tipo</TableCell>
                <TableCell>Serie / serial</TableCell>
                <TableCell>Descripción</TableCell>
                {!puedeRecibir && <TableCell>Recibido</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {d.items.map((i) => (
                <TableRow key={i.id}>
                  {puedeRecibir && (
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={!!marcados[i.id]} onChange={(e) => setMarcados((p) => ({ ...p, [i.id]: e.target.checked }))} />
                    </TableCell>
                  )}
                  <TableCell>{i.tipo_item === "EQUIPO" ? "Equipo" : "Viñeta"}</TableCell>
                  <TableCell>{i.referencia}</TableCell>
                  <TableCell>{[i.descripcion, i.tipo].filter(Boolean).join(" · ")}</TableCell>
                  {!puedeRecibir && <TableCell>{i.recibido === null ? "—" : (i.recibido ? "Sí" : "No")}</TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {puedeRecibir && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Confirmar recepción {sinMarcar > 0 ? `(${sinMarcar} sin recibir: volverán a la bodega central)` : ""}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField fullWidth size="small" label="Quién recibe (opcional)" value={recibe} onChange={(e) => setRecibe(e.target.value)} />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField fullWidth size="small" label="Observaciones (obligatorias si falta algo)" value={obs} onChange={(e) => setObs(e.target.value)} />
              </Grid>
            </Grid>
          </Box>
        )}
        {anulando && (
          <TextField fullWidth size="small" label="Motivo de la anulación" value={motivo} onChange={(e) => setMotivo(e.target.value)} sx={{ mt: 2 }} />
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={descargarActa}>Descargar acta (PDF)</Button>
        {puedeAnular && !anulando && (<Button color="warning" onClick={() => setAnulando(true)}>Anular envío</Button>)}
        {puedeAnular && anulando && (<Button color="warning" variant="contained" onClick={anular} disabled={trabajando}>Confirmar anulación</Button>)}
        {puedeRecibir && (<Button variant="contained" onClick={confirmar} disabled={trabajando}>{trabajando ? "Guardando..." : "Confirmar recepción"}</Button>)}
        <Button onClick={onClose} disabled={trabajando}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function EnviosEquiposPanel({ ctx }) {
  const [filtroSuc, setFiltroSuc] = useState("");
  const [estado, setEstado] = useState("");
  const [texto, setTexto] = useState("");
  const q = useDebounced(texto.trim(), 350);
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(25);
  const [data, setData] = useState({ items: [], total: 0 });
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState("");
  const [version, setVersion] = useState(0);
  const [nuevo, setNuevo] = useState(false);
  const [abierto, setAbierto] = useState(null);

  useEffect(() => { setPage(0); }, [q, filtroSuc, estado]);

  useEffect(() => {
    let vivo = true;
    setCargando(true);
    setError("");
    axios.get(`${API}/envios`, { params: { sucursal_id: filtroSuc || undefined, estado: estado || undefined, q: q || undefined, page: page + 1, page_size: rows } })
      .then((r) => { if (vivo) setData(r.data); })
      .catch((e) => { if (vivo) { setData({ items: [], total: 0 }); setError(mensajeError(e, "No se pudieron cargar los envíos")); } })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [filtroSuc, estado, q, page, rows, version]);

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2, alignItems: "center" }}>
        {ctx.sucursales.length > 1 && (
          <TextField select size="small" label="Sucursal" value={filtroSuc} onChange={(e) => setFiltroSuc(e.target.value)} sx={{ minWidth: 200 }}>
            <MenuItem value="">Todas</MenuItem>
            {ctx.sucursales.map((s) => (<MenuItem key={s.id} value={s.id}>{s.nombre}</MenuItem>))}
          </TextField>
        )}
        <TextField select size="small" label="Estado" value={estado} onChange={(e) => setEstado(e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="EN_TRANSITO">En tránsito</MenuItem>
          <MenuItem value="RECIBIDO">Recibidos</MenuItem>
          <MenuItem value="ANULADO">Anulados</MenuItem>
        </TextField>
        <TextField size="small" label="Número de acta" value={texto} onChange={(e) => setTexto(e.target.value)} />
        {ctx.puede_enviar && (<Button variant="contained" onClick={() => setNuevo(true)}>Nuevo envío</Button>)}
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {aviso && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setAviso("")}>{aviso}</Alert>}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Acta</TableCell>
              <TableCell>Destino</TableCell>
              <TableCell>Fecha</TableCell>
              <TableCell align="right">Equipos</TableCell>
              <TableCell align="right">Viñetas</TableCell>
              <TableCell>Estado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cargando && (<TableRow><TableCell colSpan={6} align="center"><CircularProgress size={20} /></TableCell></TableRow>)}
            {!cargando && data.items.length === 0 && (<TableRow><TableCell colSpan={6} align="center">No hay envíos de equipos ni viñetas</TableCell></TableRow>)}
            {!cargando && data.items.map((e) => (
              <TableRow key={e.id} hover sx={{ cursor: "pointer" }} onClick={() => setAbierto(e.id)}>
                <TableCell>{e.numero}</TableCell>
                <TableCell>{e.sucursal_nombre}</TableCell>
                <TableCell>{fmtFecha(e.fecha_envio)}</TableCell>
                <TableCell align="right">{e.n_equipos}</TableCell>
                <TableCell align="right">{e.n_vinetas}</TableCell>
                <TableCell>
                  <Chip size="small" color={ESTADO_COLOR[e.estado]} label={ESTADO_TEXTO[e.estado] || e.estado} />
                  {e.con_diferencia && <Chip size="small" color="error" variant="outlined" label="Con diferencia" sx={{ ml: 1 }} />}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination component="div" count={data.total} page={page} onPageChange={(e, p) => setPage(p)}
          rowsPerPage={rows} onRowsPerPageChange={(e) => { setRows(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]} labelRowsPerPage="Filas" />
      </TableContainer>
      {nuevo && (
        <NuevoEnvioDialog ctx={ctx} onClose={() => setNuevo(false)}
          onHecho={(env) => { setNuevo(false); setAviso(`Envío ${env.numero} registrado`); setVersion((v) => v + 1); setAbierto(env.id); }} />
      )}
      {abierto && (
        <DetalleEnvioDialog ctx={ctx} envioId={abierto} onClose={() => setAbierto(null)}
          onCambio={(msg) => { setAviso(msg); setVersion((v) => v + 1); }} />
      )}
    </Box>
  );
}
